import { Directive, ElementRef, input, OnDestroy, OnInit, Renderer2, inject, PLATFORM_ID, AfterViewInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  selector: '[appScrollAnimate]',
  standalone: true
})
export class ScrollAnimateDirective implements OnInit, AfterViewInit, OnDestroy {
  // Signal-based inputs for Angular 20+
  animationClass = input<string>('animate');
  animationDelay = input<number>(100);
  threshold = input<number>(0.3);

  private observer: IntersectionObserver | null = null;
  private timeoutId: any = null;
  private hasAnimated = false;
  private platformId = inject(PLATFORM_ID);
  private initializationTimeoutId: any = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit() {
    // Add initial classes for animation setup immediately
    this.renderer.addClass(this.el.nativeElement, 'animated-element');
  }

  ngAfterViewInit() {
    // Only initialize observer in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Wait a bit after view init to ensure hydration is complete
    this.initializationTimeoutId = setTimeout(() => {
      this.initializeObserver();
    }, 50);
  }

  private initializeObserver() {
    // Only run IntersectionObserver in browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Configure IntersectionObserver options with threshold from input signal
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: this.threshold()
    };

    this.observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !this.hasAnimated) {
          this.animateElement();
        }
      });
    }, options);

    this.observer.observe(this.el.nativeElement);
  }

  // Animate element when it enters viewport
  private animateElement() {
    if (this.timeoutId !== null) return;
    this.timeoutId = setTimeout(() => {
      // Apply animation class from input signal to trigger CSS animation
      this.renderer.addClass(this.el.nativeElement, this.animationClass());

      // Force style application by setting inline styles as backup
      this.renderer.setStyle(this.el.nativeElement, 'opacity', '1');
      this.renderer.setStyle(this.el.nativeElement, 'transform', 'translateY(0)');

      this.hasAnimated = true;

      // Disconnect observer after animation to prevent re-triggering
      if (this.observer) {
        this.observer.unobserve(this.el.nativeElement);
      }

      this.clearTimeout();
    }, this.animationDelay());
  }

  private clearTimeout() {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  ngOnDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
    this.clearTimeout();
    if (this.initializationTimeoutId !== null) {
      clearTimeout(this.initializationTimeoutId);
      this.initializationTimeoutId = null;
    }
  }
}
